import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
} from "@angular/core";
import { UTCDate } from "@ankaadia/ankaadia-shared";
import { UserEnvironmentService } from "../../features/user-environment/user-environment.service";
import { AppDatePipe } from "../../shared/pipes/date.pipe";
import { SettingsService } from "../../shared/services/settings.service";
import { TranslateDirective } from "../../shared/transloco/translate.directive";

const BANNER_CLASS = "has-countdown-banner";
const BANNER_HEIGHT_VAR = "--countdown-banner-height";

@Component({
  selector: "app-old-ui-countdown-banner",
  templateUrl: "./old-ui-countdown-banner.component.html",
  styleUrl: "./old-ui-countdown-banner.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateDirective, AppDatePipe],
})
export class OldUiCountdownBannerComponent implements AfterViewInit, OnDestroy {
  private readonly settings = inject(SettingsService);
  private readonly userEnvironmentService = inject(UserEnvironmentService);
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef);
  private resizeObserver: ResizeObserver | null = null;

  protected readonly OLD_UI_DISABLE_DATE = UTCDate.parse("2026-07-16");
  protected readonly daysRemaining = Math.max(0, UTCDate.today().diffInDays(this.OLD_UI_DISABLE_DATE));
  protected readonly isVisible = !this.settings.isCandidate && this.settings.isNewUiEnabled && this.daysRemaining >= 0;
  protected readonly dismissed = signal(false);

  constructor() {
    effect(() => {
      const wrapper = this.document.querySelector(".layout-wrapper");
      if (wrapper) {
        if (this.isVisible && !this.dismissed()) {
          wrapper.classList.add(BANNER_CLASS);
        } else {
          wrapper.classList.remove(BANNER_CLASS);
          this.setHeightVar(0);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      this.setHeightVar(entries[0]?.contentRect.height ?? 0);
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    const wrapper = this.document.querySelector(".layout-wrapper");
    wrapper?.classList.remove(BANNER_CLASS);
    this.setHeightVar(0);
  }

  protected switchToNewUi(): void {
    this.userEnvironmentService
      .userPrefersNewUi(true)
      .subscribe(() => this.userEnvironmentService.navigateToAlternativeEnvironment());
  }

  protected dismiss(): void {
    this.dismissed.set(true);
  }

  private setHeightVar(height: number): void {
    const wrapper = this.document.querySelector<HTMLElement>(".layout-wrapper");
    wrapper?.style.setProperty(BANNER_HEIGHT_VAR, `${height}px`);
  }
}
